import { useEffect, useState } from 'react';
import { BrainCircuit20Regular, Warning20Regular } from '@fluentui/react-icons';
import { extractBudgetDocuments, getAiSettings } from '../orcamentos/orcamentosApi';
import ExtractionProgressBanner from './ExtractionProgressBanner';

interface DocumentoItem {
  category?: string | null;
  technicalDocumentStatus?: string | null;
}

interface ExtractBudgetDocumentsButtonProps {
  budgetId: number;
  documentos: DocumentoItem[];
  onSent?: () => void;
  onOpenSettings?: () => void;
}

export default function ExtractBudgetDocumentsButton({
  budgetId,
  documentos,
  onSent,
  onOpenSettings,
}: ExtractBudgetDocumentsButtonProps) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [sent, setSent] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null);

  useEffect(() => {
    getAiSettings()
      .then((s) => setHasApiKey(s.hasAnthropicApiKey))
      .catch(() => setHasApiKey(null)); // se falhar, não bloquear
  }, []);

  const hasPendingDocs = documentos.some(
    (doc) =>
      (doc.category === 'Projeto' || doc.category === 'technical') &&
      (doc.technicalDocumentStatus === 'pending' || doc.technicalDocumentStatus === null),
  );

  const hasActiveProcessing = documentos.some(
    (doc) =>
      (doc.category === 'Projeto' || doc.category === 'technical') &&
      ['analyzing', 'generating'].includes((doc.technicalDocumentStatus ?? '').toLowerCase()),
  );

  const handleExtract = async () => {
    setIsExtracting(true);
    setError(null);
    try {
      await extractBudgetDocuments(budgetId);
      setSent(true);
      setStartedAt(Date.now());
      onSent?.();
    } catch (err: unknown) {
      const errorMsg =
        err instanceof Error ? err.message : 'Erro ao iniciar extracção. Tente novamente.';
      setError(errorMsg);
    } finally {
      setIsExtracting(false);
    }
  };

  if (!hasPendingDocs && !sent && !hasActiveProcessing) {
    return null;
  }

  if (sent || hasActiveProcessing) {
    return <ExtractionProgressBanner documentos={documentos} startedAt={startedAt} />;
  }

  // Aviso se API Key não configurada
  if (hasApiKey === false) {
    return (
      <div
        className="flex items-start gap-3 px-4 py-3 w-full"
        style={{ borderRadius: 'var(--radius)', border: '1px solid #fbbf24', backgroundColor: '#fffbeb' }}
      >
        <Warning20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
        <div>
          <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
            API Key da Anthropic não configurada
          </p>
          <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
            Para processar plantas técnicas com Claude Vision, configure a API Key nas{' '}
            {onOpenSettings ? (
              <button
                type="button"
                onClick={onOpenSettings}
                className="text-primary hover:underline"
                style={{ fontSize: 'inherit' }}
              >
                Definições → Inteligência Artificial
              </button>
            ) : (
              <span>Definições → Inteligência Artificial</span>
            )}
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleExtract}
        disabled={isExtracting}
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground border border-primary rounded transition-colors flex items-center gap-2 disabled:opacity-60"
        style={{ borderRadius: 'var(--radius-button)' }}
      >
        <BrainCircuit20Regular className="w-5 h-5" />
        {isExtracting ? 'A iniciar extracção...' : 'Extrair com Claude Vision'}
      </button>
      {error && (
        <p className="text-destructive text-sm" style={{ fontSize: 'var(--text-caption)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
